<script>
import { aawzDate } from '@utils/dates/aawz-date'
import con from '@connection'
import { GlobalBus } from '@services/bus'
import { minhaCarteira, pdf, configuracoes } from '@constants/URL'
import { MODAL, RELOAD, OPCOES_FILTRO_DATA, TIPOS_RV } from '@constants/Text'
import { GRUPO_FAMILIAR_ORIGEM } from '@modules/crm/constants/GrupoFamiliar'
import { possivelJSON, getCorretoraSimple } from '@utils/Format'
import { downloadBase64 } from '@utils/Download'
import { apiWhatsappVinculo } from '@modules/aawz-intelligence-v2/api/apiWhatsappVinculo'
import { WHATSAPP_PDF_TEXT } from '@modules/aawz-intelligence-v2/constants/Text'
import {
  snackComAcaoEnviar,
  snackDeEnvio,
} from '@modules/aawz-intelligence-v2/utils/pdf-para-whatsapp'
import { funcaoIndisponivel } from '@utils/Whitelabel'
import {
  CLIENT_BENCHMARK_KEY,
  BENCHMARK_PERSONALIZADO_COLOR,
} from '@utils/clienteBenchmark'
import {
  computeDateParams,
  UNTIL_LAST_MONTH_FILTER,
} from '@modules/consolidator/utils/string-build/build-asset-statistics-url'
import {
  consumeWalletNavigationPayload,
  clearWalletNavigationQuery,
} from '@modules/consolidator/utils/navigate-to-wallet/navigate-to-wallet.js'
import WalletAlerts from '@modules/consolidator/components/wallet-alerts/wallet-alerts.vue'
import {
  WALLET_STANDALONE_FILTER_KEYS,
  applyWalletFilterDefaults,
  resolveStandaloneFilterKeys,
} from '@modules/consolidator/utils/normalize-wallet-filters/normalize-wallet-filters'
import { buildGroupLabelMap } from '@modules/consolidator/utils/wallet-modo-configs/wallet-modo-configs'
import { buildWalletAnalysisRequest } from '@modules/consolidator/utils/build-wallet-analysis-request/build-wallet-analysis-request'
import { extractClientWalletDates } from '@modules/consolidator/utils/extract-client-wallet-dates/extract-client-wallet-dates'
import {
  formatRentabilityChart,
  getAvailableBenchmarkNames,
} from '@modules/consolidator/utils/format-rentability-chart/format-rentability-chart'
import {
  formatAlocacaoEstrategicaChart,
  formatSuggestedAllocations,
  buildClassColorMap,
} from '@modules/consolidator/utils/format-alocacao-estrategica-chart/format-alocacao-estrategica-chart'
import { formatRentBenchmarkTable } from '@modules/consolidator/utils/format-rent-benchmark-table/format-rent-benchmark-table'
import { formatMonthlyReturnTable } from '@modules/consolidator/utils/format-monthly-return-table/format-monthly-return-table'
import { formatInstitutionDistribution } from '@modules/consolidator/utils/format-institution-distribution/format-institution-distribution'
import { formatLiquidezChart } from '@modules/consolidator/utils/format-liquidez-chart/format-liquidez-chart'
import { getCurrencyByInvestmentType } from '@modules/consolidator/utils/InvestmentType/InvestmentType.js'
import { formatAssetsAnalysisTable } from '@modules/consolidator/utils/format-assets-analysis-table/format-assets-analysis-table'
import { enrichWalletWithMembers } from '@modules/consolidator/utils/enrich-wallet-with-members/enrich-wallet-with-members'
import { resolveGroupBenchmarkLabel } from '@modules/consolidator/utils/group-wallet-labels/group-wallet-labels'
import { resolveGroupMembers } from '@modules/consolidator/utils/group-member-rows/group-member-rows'
import {
  resolveClassRowBenchmark,
  buildBenchmarkMapper,
} from '@modules/consolidator/utils/resolve-group-benchmark/resolve-group-benchmark'
import { hasWalletData } from '@modules/consolidator/utils/has-wallet-data/has-wallet-data'
import { apiSettingsBenchmarks } from '@modules/settings/api/apiSettingsBenchmarks'
import { apiConsolidatorPortfolioComparePdf } from '@modules/consolidator/api/apiConsolidatorPortfolioComparePdf'
import { apiConsolidatorWalletAnalysis } from '@modules/consolidator/api/apiConsolidatorWalletAnalysis'
import { apiConsolidatorStatisticsPerAsset } from '@modules/consolidator/api/apiConsolidatorStatisticsPerAsset'
import { apiConsolidatorCotizacao } from '@modules/consolidator/api/apiConsolidatorCotizacao'
import { apiRequisicoes } from '@modules/requisicoes/api/apiRequisicoes'
import WalletFilters from '@modules/consolidator/components/wallet-filters/wallet-filters.vue'
import ConditionalTeleport from '@components/teleport/conditional-teleport.vue'
import FeedbackWrapper from '@modules/global/components/feedback-wrapper/feedback-wrapper.vue'
import WalletEmptyState from '@modules/consolidator/components/wallet-empty-state/wallet-empty-state.vue'
import ConsolidatorSelectClient from '@modules/consolidator/modal/select-client/select-client.vue'
import walletEmptyStateActions from '@modules/consolidator/mixins/wallet-empty-state-actions'
import AutocompleteMany from '@components/autocomplete-many/autocomplete-many.vue'
import TabelaRentBenchmark from '@components/tabela-rent-benchmark'
import AlocacaoEstrategica from '@modules/consolidator/components/alocacao-estrategica/alocacao-estrategica.vue'
import PortfolioBenchmark from '@modules/consolidator/components/portfolio-benchmark/portfolio-benchmark.vue'
import LiquidezChart from '@modules/consolidator/components/liquidez-chart/liquidez-chart.vue'
import Distribuicao from '@modules/consolidator/components/distribuicao/distribuicao.vue'
import TabelaRetornoMensais from '@components/tabela-retornos-mensais'
import AssetsAnalysisTable from '@modules/consolidator/components/assets-analysis-table/assets-analysis-table.vue'
import { resolveWalletScope } from '@modules/consolidator/utils/wallet-scope/wallet-scope'

// Colunas de filtro que o relatório PDF repassa ao /consolidation — mesmo
// conjunto aceito pelo backend em resource_portfolio_consolidation
// (FILTER_LIST_STR_COLUMNS + FILTER_LIST_INT_COLUMNS).
//
// `investment_type` NÃO entra nesta lista, e não por esquecimento: aqui só
// cabem filtros por COLUNA do catálogo (`{coluna: [valores]}`). O tipo de
// investimento é um escalar que o engine recebe como `tipo_investimento` e que
// governa o universo de ativos E a moeda — vai em campo próprio, via
// `getPDFReportInvestmentType()`. Dentro deste dict ele seria descartado pela
// whitelist de colunas do backend.
//
// Até 2026-08 ele era omitido DE PROPÓSITO ("o relatório já tem seções
// onshore/offshore próprias"). Estava errado: as seções dedicadas existirem
// não faz o RESTO do relatório respeitar o filtro, e o gestor que escolhia
// Offshore recebia a carteira inteira (CC-0r06clx2).
const PDF_REPORT_FILTER_COLUMNS = [
  'corretora',
  'tipo',
  'subtipo',
  'classe',
  'subclasse',
  'nome_portfolio',
  'emissor',
  'tags',
  'setor',
  'id_conta',
]

export default {
  page: {
    title: 'Análise Porfólio',
    meta: [{ name: 'description', content: 'CarteiraAnalise' }],
  },
  components: {
    WalletFilters,
    ConditionalTeleport,
    FeedbackWrapper,
    WalletEmptyState,
    ConsolidatorSelectClient,
    AutocompleteMany,
    TabelaRentBenchmark,
    AlocacaoEstrategica,
    PortfolioBenchmark,
    LiquidezChart,
    Distribuicao,
    TabelaRetornoMensais,
    AssetsAnalysisTable,
    WalletAlerts,
  },
  mixins: [walletEmptyStateActions],
  props: {
    bus: {
      type: Object,
      default: null,
    },
    showActions: {
      type: Boolean,
    },
    clienteModal: {
      type: Object,
      default: null,
    },
    busLeft: {
      type: Object,
      default: null,
      required: true,
    },
  },
  data() {
    return {
      TIPOS_RV,
      OPCOES_FILTRO_DATA,
      show_filters: false,
      clients: [],
      loadingClients: false,
      isWhitelabel: this.$isWhitelabel(),
      SHOW_WORKSPACE: this.$showWorkspace,
      loading: false,
      loadingPDF: false,
      loadingComparePdf: false,
      CLIENT_BENCHMARK_KEY,
      BENCHMARK_PERSONALIZADO_COLOR,
      ativos: {},
      benchmarks_selecionados: [],
      benchmarks_selecionados_grafico: [],
      benchmarks: [],
      benchmark_graph_changed: false,
      benchmark_statistics_change: false,
      mapper_benchmarks: {},
      groupAssets: false,
      loadingAssets: false,
      noDataMessage: 'Sem dados no momento.',
      compareRequestId: null,
      comparePollingInterval: null,
      pdfPollTimeout: null,
      PDF_POLL_INTERVAL_MS: 3000,
      navigationPayloadConsumido: false,
    }
  },
  computed: {
    whatsappConectado() {
      return this.$store.getters['aawz-intelligence-v2/whatsappConectado']
    },
    walletScope() {
      return resolveWalletScope({ idCliente: this.id_cliente })
    },
    moduleRoutes() {
      return [{ name: 'carteira.analise', title: 'Análise portfólio' }]
    },
    reportActions() {
      return [
        {
          label: 'Comparar PDF',
          icon: 'document-simple',
          loading: this.loadingComparePdf,
          onClick: this.openComparePdfInput,
        },
      ]
    },
    showReportActions() {
      return (
        this.walletScope.tipo === 'cliente' &&
        !this.isClient &&
        (this.isLoadingData || !this.isEmpty)
      )
    },
    filtro_corretora() {
      return this.$store.getters['consolidator/walletFiltersCorretora']
    },
    filtro_instituicao() {
      return this.$store.getters['consolidator/walletFiltersInstituicao']
    },
    filtro_portfolio() {
      return this.$store.getters['consolidator/walletFiltersPortfolio']
    },
    filtro_data() {
      return this.$store.getters['consolidator/walletFiltersData']
    },
    modo_filtro_corretora() {
      return this.$store.getters['consolidator/walletFiltersModo']
    },
    dataInicio() {
      return this.$store.getters['consolidator/walletFiltersDataInicio']
    },
    dataFim() {
      return this.$store.getters['consolidator/walletFiltersDataFim']
    },
    walletFilterFields() {
      const opts = Object.values(this.OPCOES_FILTRO_DATA)
      const especificaIndex = opts.indexOf(this.OPCOES_FILTRO_DATA.ESPECIFICA)
      const filtroDataItems =
        especificaIndex < 0
          ? [...opts, UNTIL_LAST_MONTH_FILTER]
          : [
              ...opts.slice(0, especificaIndex),
              UNTIL_LAST_MONTH_FILTER,
              ...opts.slice(especificaIndex),
            ]
      return [
        { key: 'modoFiltroCorretora' },
        { key: 'filtroCorretora' },
        { key: 'investmentTypeFilter' },
        {
          key: 'filtroData',
          items: filtroDataItems,
          default: this.OPCOES_FILTRO_DATA.ANO,
        },
      ]
    },
    enabledWalletFilterKeys() {
      return [
        ...this.walletFilterFields.map((field) => field.key),
        ...resolveStandaloneFilterKeys(this.modo_filtro_corretora),
      ]
    },
    menuWalletFilterKeys() {
      return [
        ...this.walletFilterFields.map((field) => field.key),
        ...WALLET_STANDALONE_FILTER_KEYS,
      ]
    },
    investmentTypeFilter() {
      return this.$store.getters['consolidator/walletFiltersInvestmentType']
    },
    requestInvestmentTypeFilter() {
      return this.investmentTypeFilter || 'Todos'
    },
    displayInvestmentTypeFilter() {
      if (!this.investmentTypeFilter || this.investmentTypeFilter === 'Todos') {
        return ''
      }
      return this.investmentTypeFilter
    },
    showCustomDate() {
      return this.filtro_data === OPCOES_FILTRO_DATA.ESPECIFICA
    },
    walletAnalysis() {
      return this.$store.getters['consolidator/walletAnalysis']
    },
    walletAnalysisOptionsFields() {
      return this.$store.getters['consolidator/walletAnalysisOptionsFields']
    },
    groupLabelByKey() {
      return buildGroupLabelMap({
        modo: this.modo_filtro_corretora,
        optionsFields: this.walletAnalysisOptionsFields,
      })
    },
    isLoadingData() {
      return this.loading || this.walletAnalysisLoading
    },
    walletAnalysisLoading() {
      return !!this.$store.getters['consolidator/walletAnalysisLoading']
    },
    walletAlertsContext() {
      return {
        screen: 'analise',
        idCliente: this.id_cliente,
        isClient: this.isClient,
        isFamilyGroup: !!this.walletScope.grupo,
        walletAnalysis: this.walletAnalysis,
        dataOutdated:
          this.walletScope.tipo === 'grupo'
            ? false
            : this.$store.getters['consolidator/clientDataOutdated'],
        loading: this.isLoadingData,
        loadingClients: this.loadingClients,
      }
    },
    dataFimReferencia() {
      const stats =
        this.walletAnalysis && this.walletAnalysis.portfolio_statistics
      return (stats && stats.data_fim) || ''
    },
    hasIndicatorsData() {
      return !!(this.walletAnalysis && this.walletAnalysis.portfolio_statistics)
    },
    clienteBenchmarkMeta() {
      return (
        (this.walletAnalysis && this.walletAnalysis.cliente_benchmark_meta) ||
        null
      )
    },
    selectedBenchmarksGrafico() {
      const ids = Array.isArray(this.benchmarks_selecionados_grafico)
        ? this.benchmarks_selecionados_grafico
        : []
      return this.BENCHMARKS.filter((b) => ids.includes(b.id))
    },
    rentabilityChart() {
      return formatRentabilityChart(this.walletAnalysis, {
        primaryColor: this.Color.primary,
        getBankColor: (legend) => this.getBankColor(legend),
        getCorretoraSimple,
        groupBy: this.modo_filtro_corretora,
        groupLabelByKey: this.groupLabelByKey,
        selectedBenchmarks: this.selectedBenchmarksGrafico,
        clienteBenchmarkMeta: this.clienteBenchmarkMeta,
        isClienteBenchmarkSelected:
          this.benchmarks_selecionados_grafico.includes(CLIENT_BENCHMARK_KEY),
      })
    },
    availableBenchmarkNames() {
      return getAvailableBenchmarkNames(this.walletAnalysis)
    },
    alocacaoEstrategicaChart() {
      return formatAlocacaoEstrategicaChart(
        this.walletAnalysis,
        (name, index) => this.getClassColor(name, index),
        {
          groupBy: this.modo_filtro_corretora,
          groupLabelByKey: this.groupLabelByKey,
        }
      )
    },
    rentBenchmarkTable() {
      return formatRentBenchmarkTable(this.walletAnalysis, {
        benchmarks: this.BENCHMARKS,
      })
    },
    alocacaoChartValues() {
      return this.alocacaoEstrategicaChart.values
    },
    alocacaoChartOpts() {
      return this.alocacaoEstrategicaChart.opts
    },
    allocationColorsByLabel() {
      return buildClassColorMap(
        (this.alocacaoChartOpts && this.alocacaoChartOpts.labels) || []
      )
    },
    suggestedAllocations() {
      const labels =
        (this.alocacaoChartOpts && this.alocacaoChartOpts.labels) || []
      return formatSuggestedAllocations(this.walletAnalysis, labels)
    },
    institutionDistributionData() {
      return formatInstitutionDistribution(this.walletAnalysis)
    },
    liquidezChartData() {
      return formatLiquidezChart(this.walletAnalysis, {
        currency: this.walletCurrency,
      })
    },
    walletCurrency() {
      return getCurrencyByInvestmentType({
        investmentType: this.displayInvestmentTypeFilter,
      })
    },
    retornoMensalTable() {
      return formatMonthlyReturnTable(this.walletAnalysis)
    },
    membrosDoGrupo() {
      return resolveGroupMembers()
    },
    carteiraDaAnalise() {
      if (this.walletScope.tipo !== 'grupo') return this.walletAnalysis
      return enrichWalletWithMembers(this.walletAnalysis, this.membrosDoGrupo)
    },
    assetsAnalysisData() {
      return formatAssetsAnalysisTable(
        this.carteiraDaAnalise,
        this.$store.getters['consolidator/statisticsPerAsset'],
        { currency: this.walletCurrency }
      )
    },
    benchmarkDoGrupoFamiliar() {
      if (this.walletScope.tipo !== 'grupo') return ''
      const wallet = this.walletAnalysis
      return resolveGroupBenchmarkLabel(wallet && wallet.grupo)
    },
    classBenchmarkMap() {
      const analysis = this.walletAnalysis
      const hasGrupos =
        analysis &&
        analysis.posicao_completa &&
        Array.isArray(analysis.posicao_completa.grupos)
      if (!hasGrupos) return {}
      const breakdownData =
        (analysis.breakdown && analysis.breakdown.data) || {}
      const map = {}
      for (const grupo of analysis.posicao_completa.grupos) {
        if (!grupo || !grupo.grupo) continue
        map[grupo.grupo] = resolveClassRowBenchmark({
          grupo,
          breakdownData,
          groupBenchmark: this.benchmarkDoGrupoFamiliar,
          mapper: this.mapper_benchmarks,
        })
      }
      return map
    },
    BENCHMARKS() {
      const benchs = this.$store.getters['settings/benchmarks']
      const stats =
        this.walletAnalysis &&
        this.walletAnalysis.portfolio_statistics &&
        this.walletAnalysis.portfolio_statistics.benchmarks_statistics
      if (!stats || Object.keys(stats).length === 0) return benchs
      return benchs.filter((bench) => stats[bench.id] || stats[bench.nome])
    },
    usuarioAtual() {
      return this.$store.getters['user/currentUser'] || {}
    },
    clienteAtual() {
      return (
        this.clienteModal || this.$store.getters['user/currentClient'] || {}
      )
    },
    Color() {
      return this.$store.getters['parameters/colors'] || {}
    },
    id_cliente() {
      return (
        // this.$route.params.id_cliente ||
        this.clienteAtual.id
      )
    },
    isEmpty() {
      return !hasWalletData(this.walletAnalysis)
    },
    hasClients() {
      return this.clients.length !== 0
    },
    hasSelectedClient() {
      return this.walletScope.ativo
    },
    hasAcess() {
      return typeof this.id_cliente === 'string' ? false : this.id_cliente <= 0
    },
    hasClient() {
      return this.walletScope.ativo
    },
    clientNotSelected() {
      if (this.isClient) return false
      return !this.hasSelectedClient
    },
    showWalletEmptyState() {
      if (this.clientNotSelected) return true
      if (this.isLoadingData) return false
      return this.isEmpty
    },
    isClient() {
      return !!this.$currentUser().IS_CLIENTE
    },
  },
  watch: {
    show_filters(newVal) {
      if (newVal && this.$refs.slideGroup) {
        this.$refs.slideGroup.setWidths()
      }
    },
    modo_filtro_corretora(newVal, oldVal) {
      if (!oldVal) return
      this.loading = true
      this.refreshAfterFilterChange()
    },
    filtro_corretora(newVal, oldVal) {
      if (!oldVal) return
      this.loading = true
      this.refreshAfterFilterChange()
    },
    investmentTypeFilter(newVal, oldVal) {
      if (!oldVal) return
      this.reloadStatistics({ ignoreEmptySpecificDate: true })
    },
  },
  created() {
    this.getDados()
    this.loadBenchmarks()
    this.carregarVinculoWhatsapp()
    GlobalBus.$on('changeClient', this.handleClientChange)
    this.bus.$on('imprimir-analise-portfolio', this.imprimir)
    GlobalBus.$on(MODAL.OPEN_SEND_COMPONENT, this.openSendComponent)
    GlobalBus.$on(RELOAD.ASSETS, this.getDados)
    GlobalBus.$on(RELOAD.ASSETS_ONLY, this.loadWalletFilterOptions)
    GlobalBus.$on(RELOAD.EDIT_ASSET, this.loadWalletFilterOptions)
    GlobalBus.$on(RELOAD.MANAGE_BENCHMARKS, this.onBenchmarkChanged)

    apiSettingsBenchmarks.carregar()
  },
  beforeDestroy() {
    GlobalBus.$off('changeClient', this.handleClientChange)
    this.bus.$off('imprimir-analise-portfolio', this.imprimir)
    GlobalBus.$off(MODAL.OPEN_SEND_COMPONENT, this.openSendComponent)
    GlobalBus.$off(RELOAD.ASSETS, this.getDados)
    GlobalBus.$off(RELOAD.ASSETS_ONLY, this.loadWalletFilterOptions)
    GlobalBus.$off(RELOAD.EDIT_ASSET, this.loadWalletFilterOptions)
    GlobalBus.$off(RELOAD.MANAGE_BENCHMARKS, this.onBenchmarkChanged)
    this.stopComparePolling()
    clearTimeout(this.pdfPollTimeout)
  },
  methods: {
    // Sabe "tem WhatsApp conectado?" antes do primeiro PDF; uma leitura por sessão.
    // Método (e não inline no created) porque testes antigos chamam `created`
    // com um contexto parcial, sem `$store`.
    carregarVinculoWhatsapp() {
      apiWhatsappVinculo.garantir({
        user: this.$currentUser ? this.$currentUser() : {},
        access: this.$access,
      })
    },
    abrirNovoGrupo() {
      GlobalBus.$emit(MODAL.CRM_GRUPO_FAMILIAR_MODAL, {
        item: {},
        origem: GRUPO_FAMILIAR_ORIGEM.LISTAGEM,
      })
    },
    openSendComponent() {
      // if (!this.ClienteModal) {
      GlobalBus.$emit(
        MODAL.WORKSPACE_SEND_COMPONENT,
        {
          page: this.$route.name,
          data: {
            id_cliente: this.$currentClient().id,
          },
        },
        this.$currentClient()
      )
      // }
    },
    handleClientChange() {
      this.$store.dispatch('consolidator/resetWalletFilters')
      this.getDados()
    },
    loadWalletFilterOptions() {
      if (!this.walletScope.ativo) return
      apiConsolidatorWalletAnalysis.loadOptions({
        id_cliente_app: this.clienteAtual.id,
        is_client: this.$currentUser().IS_CLIENTE,
      })
    },
    getDados() {
      this.collectClients()
      this.clearData()
      const navigationPayload = this.consumeNavigationPayload()
      this.applyNavigationFiltersFromPayload({ payload: navigationPayload })
      this.normalizeWalletFilters()
      if (!this.walletScope.ativo) {
        return
      }
      this.loading = true
      this.loadWalletFilterOptions()
      this.loadWalletAnalysis()
      this.loadStatisticsPerAsset()
      this.clearNavigationQueryAfterLoad({ payload: navigationPayload })
      this.loadBenchmarks()
    },
    collectClients() {
      if (this.usuarioAtual.IS_CLIENTE) {
        return
      }
      this.loadingClients = true
      con.post(minhaCarteira.listar_clientes).then((r) => {
        if (r.status === 200) {
          this.clients = r.value || []
        }
        this.loadingClients = false
      })
    },
    consumeNavigationPayload() {
      if (this.navigationPayloadConsumido) return null
      const payload = consumeWalletNavigationPayload({ route: this.$route })
      if (payload) this.navigationPayloadConsumido = true
      return payload
    },
    applyNavigationFiltersFromPayload({ payload }) {
      const filters = payload ? payload.filters || {} : {}
      this.applyBrokerNavigationFilter({ filters })
      this.applyDateNavigationFilter({ filters })
    },
    applyBrokerNavigationFilter({ filters }) {
      const brokerName = filters.brokerName || ''
      if (!brokerName) return
      this.$store.dispatch('consolidator/setWalletFiltersModo', 'subclasse')
      this.$store.dispatch('consolidator/setWalletFiltersInstituicao', [
        brokerName,
      ])
      this.$store.dispatch('consolidator/setWalletFiltersUserChangedField', {
        key: 'corretora',
        value: true,
      })
    },
    applyDateNavigationFilter({ filters }) {
      const dateOption = this.resolveDateOption({ filters })
      if (dateOption) {
        this.$store.dispatch('consolidator/setWalletFiltersData', dateOption)
      }
      if (this.filtro_data !== OPCOES_FILTRO_DATA.ESPECIFICA) return
      const dateRange = this.resolveNavigationRange({ filters })
      if (!dateRange) return
      this.$store.dispatch(
        'consolidator/setWalletFiltersDataInicio',
        dateRange.beginDate
      )
      this.$store.dispatch(
        'consolidator/setWalletFiltersDataFim',
        dateRange.endDate
      )
      this.$store.dispatch('consolidator/setWalletFiltersUserChangedField', {
        key: 'filtroData',
        value: true,
      })
    },
    resolveDateOption({ filters }) {
      const dateOption = filters.dateOption || ''
      if (dateOption) return dateOption
      if (filters.beginDate && filters.endDate) {
        return OPCOES_FILTRO_DATA.ESPECIFICA
      }
      return ''
    },
    resolveNavigationRange({ filters }) {
      const beginDate = this.normalizePickerDate({ value: filters.beginDate })
      const endDate = this.normalizePickerDate({ value: filters.endDate })
      if (!beginDate || !endDate) return null
      return this.clampDateRangeToToday({ beginDate, endDate })
    },
    normalizePickerDate({ value }) {
      const textValue = String(value || '').trim()
      if (!textValue) return ''
      const inputFormats = ['YYYY-MM-DD', 'DD-MM-YYYY', 'DD/MM/YYYY']
      for (const inputFormat of inputFormats) {
        const parsed = aawzDate(textValue, inputFormat)
        if (parsed.isValid()) {
          return parsed.format('YYYY-MM-DD')
        }
      }
      return ''
    },
    clampDateRangeToToday({ beginDate, endDate }) {
      const todayValue = aawzDate().format('YYYY-MM-DD')
      const endResolved = endDate > todayValue ? todayValue : endDate
      if (beginDate > endResolved) return null
      return { beginDate, endDate: endResolved }
    },
    clearNavigationQueryAfterLoad({ payload }) {
      if (!payload) return
      clearWalletNavigationQuery({ route: this.$route, router: this.$router })
    },
    getBankColor(classe, index) {
      return this.$store.getters['parameters/bankColor'](classe, index)
    },
    clearData() {
      this.ativos = {}
      this.benchmark_graph_changed = false
      this.benchmark_statistics_change = false
      this.benchmarks_selecionados = []
      this.benchmarks_selecionados_grafico = []
      this.groupAssets = false
      this.loadingAssets = false
    },
    async getAllData() {
      const dateParams = computeDateParams({
        filtroData: this.filtro_data,
        dataInicio: this.dataInicio,
        dataFim: this.dataFim,
      })
      let dates = ''
      if (dateParams.begin_date) {
        dates += `&begin_date=${dateParams.begin_date}`
      }
      if (dateParams.end_date) {
        dates += `&end_date=${dateParams.end_date}`
      }
      let url = ''
      if (this.$currentUser().IS_CLIENTE) {
        url += minhaCarteira.cliente.analise_portfolio
      } else {
        url += minhaCarteira.analise_portfolio + `/${this.id_cliente}`
      }
      const legacyCorretora = Array.isArray(this.filtro_corretora)
        ? this.filtro_corretora[0] || ''
        : this.filtro_corretora || ''
      url += `?report_parts=assets_statistics&corretora=${encodeURIComponent(
        legacyCorretora
      )}${dates}`
      url += `&group_by=${this.modo_filtro_corretora}`
      url += '&group_assets_by=subclasse'
      const res = await con.get(url)

      if (res && res.status === 200) {
        let assets = possivelJSON(res.value.assets_statistics) || {}
        if (assets && Object.keys(assets).includes('total_pl')) {
          const pl = assets['total_pl']
          delete assets.total_pl
          assets.total_pl = pl
        }
        this.ativos = assets
      }

      this.loading = false
    },
    getClassColor(classe, index) {
      return this.$store.getters['parameters/paleta'][index]
    },
    onReportAction(item) {
      if (item && typeof item.onClick === 'function') {
        item.onClick()
      }
    },
    imprimir() {
      if (this.isWhitelabel) {
        this.enviarGerarPDF()
      } else {
        funcaoIndisponivel(this)
      }
    },
    async enviarGerarPDF() {
      if (this.$currentUser().IS_CLIENTE) {
        return
      }
      if (this.loadingPDF) {
        return
      }
      const data = {
        corretora: this.filtro_corretora,
        benchmarks_selection: JSON.stringify({
          graph_portfolio_return: this.benchmarks_selecionados_grafico, // grafico
          portfolio_statistics: this.benchmarks_selecionados, // estatistica resumida
        }),
        group_assets_by: 'subclasse',
      }

      const dateParams = computeDateParams({
        filtroData: this.filtro_data,
        dataInicio: this.dataInicio,
        dataFim: this.dataFim,
      })
      if (dateParams.begin_date) {
        data.begin_date = dateParams.begin_date
      }
      if (dateParams.end_date) {
        data.end_date = dateParams.end_date
      }

      this.loadingPDF = true
      this.bus.$emit('set-loading-imprimir', true)
      const r = await con.post(
        pdf.run + this.clienteAtual.id + '?group_assets_by=subclasse',
        data
      )
      if (r.status === 200) {
        this.checkStatusPDF(r.value.hash, r.value.id_cliente_app)
      } else {
        this.$showSnack({
          text: r.message,
          button: true,
        })
        this.loadingPDF = false
        this.bus.$emit('set-loading-imprimir', false)
      }
    },
    async checkStatusPDF(hash, id_cliente) {
      if (
        this._isDestroyed ||
        this._isBeingDestroyed ||
        this.$currentUser().IS_CLIENTE
      ) {
        return
      }
      const r = await con.get(pdf.get + `${id_cliente}/${hash}`)
      if (this._isDestroyed || this._isBeingDestroyed) {
        return
      }
      this.handlePdfStatusResponse(r, hash, id_cliente)
    },
    handlePdfStatusResponse(r, hash, id_cliente) {
      if (r.status === 200 && r.step === -1) {
        this.baixarPDF(hash, id_cliente)
        this.loadingPDF = false
        this.bus.$emit('set-loading-imprimir', false)
      } else if (r.status === 200) {
        this.schedulePdfStatusPoll(hash, id_cliente)
      } else {
        this.loadingPDF = false
        this.bus.$emit('set-loading-imprimir', false)
        this.$showSnack({
          text: r.message,
          button: true,
        })
      }
    },
    schedulePdfStatusPoll(hash, id_cliente) {
      this.pdfPollTimeout = setTimeout(() => {
        this.checkStatusPDF(hash, id_cliente)
      }, this.PDF_POLL_INTERVAL_MS)
    },
    baixarPDF(hash, id_cliente) {
      // window.open(
      //   window.location.origin + pdf.get + `${id_cliente}/${hash}?download=true`
      // )
      this.downloadPDFbase64(hash, id_cliente)

      // window.location.href =
      //   window.location.origin + pdf.get + `${id_cliente}/${hash}?download=true`
    },
    async downloadPDFbase64(hash, id_cliente) {
      const url =
        pdf.get + `${id_cliente}/${hash}?download=true&with_base64=true`
      const r = await con.get(url)

      if (r.status === 200) {
        downloadBase64(r.value.base64, r.value.name, r.value.extension)
        // Com WhatsApp conectado à IA, o mesmo PDF pode ir para o celular do
        // profissional sem regerar: o backend reenvia o arquivo do S3 pelo hash.
        if (this.whatsappConectado) {
          this.$showSnack(
            snackComAcaoEnviar({
              texto: 'PDF baixado.',
              onEnviar: () => this.enviarPdfWhatsapp(hash, id_cliente),
            })
          )
        }
      } else {
        this.$showSnack({
          text: 'Não foi possível fazer o download, tente novamente mais tarde',
          button: true,
        })
      }
    },
    async enviarPdfWhatsapp(hash, id_cliente) {
      // Feedback imediato: o envio passa pelo S3 e pelo Evolution e leva segundos.
      this.$showSnack({ text: WHATSAPP_PDF_TEXT.enviando, button: false })
      const r = await apiWhatsappVinculo.enviarPdf({
        idCliente: id_cliente,
        tipo: 'posicao',
        hash,
      })
      this.$showSnack(snackDeEnvio(r))
    },
    normalizeWalletFilters() {
      applyWalletFilterDefaults({
        fields: this.walletFilterFields,
        store: this.$store,
      })
    },
    handleFilterApply() {
      this.reloadStatistics()
    },
    reloadStatistics({ ignoreEmptySpecificDate = false } = {}) {
      if (
        !ignoreEmptySpecificDate &&
        this.filtro_data === OPCOES_FILTRO_DATA.ESPECIFICA &&
        !(this.dataInicio || this.dataFim)
      ) {
        return
      }
      this.loading = true
      this.refreshAfterFilterChange()
      this.loadStatisticsPerAsset()
    },
    refreshAfterFilterChange() {
      this.loadWalletAnalysis()
    },
    buildWalletAnalysisParams() {
      return buildWalletAnalysisRequest({
        idClienteApp: this.id_cliente,
        usuarioAtual: this.usuarioAtual,
        groupBy: this.modo_filtro_corretora,
        filtroCorretora: this.filtro_corretora,
        modoFiltroCorretora: this.modo_filtro_corretora,
        investmentTypeFilter: this.requestInvestmentTypeFilter,
        showCustomDate: this.showCustomDate,
        filtroData: this.filtro_data,
        OPCOES_FILTRO_DATA,
        dataInicio: this.dataInicio,
        dataFim: this.dataFim,
        corretora: this.filtro_instituicao,
        nome_portfolio: this.filtro_portfolio,
        enabledFieldKeys: this.enabledWalletFilterKeys,
      })
    },
    onWalletAlertAction({ id }) {
      if (id === 'atualizar') this.loadWalletAnalysis()
    },
    async loadWalletAnalysis() {
      if (!this.walletScope.ativo) return
      const params = this.buildWalletAnalysisParams()
      await apiConsolidatorWalletAnalysis.carregar({
        ...params,
        forceUpdate: true,
      })
      this.setClientWalletDates()
      this.ensureClienteBenchmarkInChart()
      this.ensureClienteBenchmarkInIndicators()
      this.loading = false
    },
    setClientWalletDates() {
      if (this.walletScope.tipo !== 'cliente') return
      const dates = extractClientWalletDates(this.walletAnalysis)
      this.$store.dispatch('user/setDatas', {
        id_cliente: this.id_cliente,
        data_atualizacao: dates.data_atualizacao,
        data_cotizacao: dates.data_cotizacao,
      })
    },
    resetGraphBenchmarks() {
      this.benchmarks_selecionados_grafico = []
      this.benchmark_graph_changed = false
      this.ensureClienteBenchmarkInChart()
    },
    ensureClienteBenchmarkInChart() {
      if (this.benchmark_graph_changed) return
      const cdi = (this.BENCHMARKS || []).find((b) => b && b.nome === 'CDI')
      if (this.clienteBenchmarkMeta) {
        if (
          !this.benchmarks_selecionados_grafico.includes(CLIENT_BENCHMARK_KEY)
        ) {
          this.benchmarks_selecionados_grafico.unshift(CLIENT_BENCHMARK_KEY)
        }
        if (cdi && this.benchmarks_selecionados_grafico.includes(cdi.id)) {
          this.benchmarks_selecionados_grafico =
            this.benchmarks_selecionados_grafico.filter((id) => id !== cdi.id)
        }
        return
      }
      if (cdi && !this.benchmarks_selecionados_grafico.includes(cdi.id)) {
        this.benchmarks_selecionados_grafico.push(cdi.id)
      }
    },
    ensureClienteBenchmarkInIndicators() {
      if (this.benchmark_statistics_change) return
      const cdi = (this.BENCHMARKS || []).find((b) => b && b.nome === 'CDI')
      const ipca = (this.BENCHMARKS || []).find((b) => b && b.nome === 'IPC-A')
      if (this.clienteBenchmarkMeta) {
        const stats =
          this.walletAnalysis &&
          this.walletAnalysis.portfolio_statistics &&
          this.walletAnalysis.portfolio_statistics.benchmarks_statistics
        if (stats && stats[CLIENT_BENCHMARK_KEY]) {
          this.addClienteBenchmarkToIndicadores()
        }
      }
      if (cdi && !this.benchmarks_selecionados.includes(cdi.id)) {
        this.benchmarks_selecionados.push(cdi.id)
      }
      if (ipca && !this.benchmarks_selecionados.includes(ipca.id)) {
        this.benchmarks_selecionados.push(ipca.id)
      }
    },
    loadStatisticsPerAsset() {
      if (!this.walletScope.ativo) return
      const params = this.buildWalletAnalysisParams()
      apiConsolidatorStatisticsPerAsset.carregar({
        ...params,
        forceUpdate: true,
      })
    },
    async loadBenchmarks() {
      const url = this.$currentUser().IS_CLIENTE
        ? configuracoes.benchmark.client_manage
        : configuracoes.benchmark.manage
      const r = await con.get(url)

      if (r.status === 200 && r.value.length) {
        this.mapper_benchmarks = buildBenchmarkMapper(r.value)
      } else {
        this.mapper_benchmarks = {}
      }
    },
    removeClienteBenchmarkFromIndicadores() {
      this.benchmarks_selecionados = this.benchmarks_selecionados.filter(
        (id) => id !== CLIENT_BENCHMARK_KEY
      )
    },
    addClienteBenchmarkToIndicadores() {
      if (!this.benchmarks_selecionados.includes(CLIENT_BENCHMARK_KEY)) {
        this.benchmarks_selecionados.unshift(CLIENT_BENCHMARK_KEY)
      }
    },
    openSelectModelPDF() {
      const data = this.getPDFReportParams()
      if (!data) return

      GlobalBus.$emit(MODAL.SELECT_PDF_MODEL, 'carteira.analise', {
        params: data,
        id_cliente: this.id_cliente,
      })
    },
    getPDFReportParams() {
      if (this.isEmpty) {
        this.$showSnack({
          text: 'Nenhum ativo com estatística. Inserir informações de pelo menos 1 ativo para emitir relatórios',
          button: true,
        })
        return null
      }

      const data = {
        id_cliente: this.id_cliente,
        corretora: this.filtro_corretora,
        group_by: this.modo_filtro_corretora,
        benchmarks_selection: this.getBenchmarkSelection(),
        filters: this.getPDFReportFilters(),
        investment_type: this.getPDFReportInvestmentType(),
      }

      const data_with_dates = this.aplicarFiltroDeDatas(data)

      return data_with_dates
    },

    // Espelha no relatório PDF os mesmos filtros de coluna que a tela de Análise
    // de Portfólio manda para a rota /consolidation: reaproveita o próprio
    // `buildWalletAnalysisParams()` (fonte única) e recorta só as colunas de
    // filtro que o backend aceita (resource_portfolio_consolidation). Sem isto,
    // o gestor filtrava 2 portfólios na tela mas o PDF vinha com a carteira
    // inteira.
    getPDFReportFilters() {
      const walletParams = this.buildWalletAnalysisParams()
      const filters = {}
      for (const coluna of PDF_REPORT_FILTER_COLUMNS) {
        const valor = walletParams[coluna]
        if (valor !== undefined && valor !== null) filters[coluna] = valor
      }
      return filters
    },

    // Tipo de investimento (onshore/offshore) selecionado na tela. Sai do MESMO
    // `buildWalletAnalysisParams()` que alimenta o /consolidation — é o que
    // garante que o PDF filtre exatamente o que a tela mostrou. Sem seleção,
    // `null`: relatório completo, como sempre foi.
    getPDFReportInvestmentType() {
      return this.buildWalletAnalysisParams().investment_type || null
    },

    getBenchmarkSelection() {
      const benchmarks = {}
      if (this.benchmark_graph_changed) {
        benchmarks.graph_portfolio_return = this.benchmarks_selecionados_grafico
      }
      if (this.benchmark_statistics_change) {
        benchmarks.portfolio_statistics = this.benchmarks_selecionados
      }
      return benchmarks
    },

    aplicarFiltroDeDatas(data) {
      const dateParams = computeDateParams({
        filtroData: this.filtro_data,
        dataInicio: this.dataInicio,
        dataFim: this.dataFim,
      })

      if (dateParams.begin_date) {
        data.begin_date = dateParams.begin_date
      }
      if (dateParams.end_date) {
        data.end_date = dateParams.end_date
      }

      return data
    },
    formatarDataCota(data) {
      const d =
        typeof data === 'string' ? aawzDate(data, 'DD/MM/YYYY') : aawzDate(data)
      if (d.isValid()) {
        return `D-${aawzDate().diff(d.toDate(), 'days')}`
      } else {
        return ''
      }
    },
    openBenchmark() {
      GlobalBus.$emit(MODAL.BENCHMARK_COMPOSITION)
    },

    async reloadAssets() {
      this.loadingAssets = true
      await this.loadWalletAnalysis()
      this.loadStatisticsPerAsset()
      await this.loadBenchmarks()
      this.loadingAssets = false
    },
    onBenchmarkChanged() {
      if (!this.id_cliente) return
      apiConsolidatorCotizacao.run({ id_cliente: this.id_cliente })
    },
    openComparePdfInput() {
      const el = document.getElementById('compare-pdf-input')
      if (el) el.click()
    },
    async handleComparePdfFile(file) {
      if (!file) return
      const el = document.getElementById('compare-pdf-input')
      if (el) el.value = ''

      if (this.loadingComparePdf) return
      this.loadingComparePdf = true

      try {
        await this.getAllData()
        const r = await apiConsolidatorPortfolioComparePdf.comparar({
          idCliente: this.id_cliente,
          file,
          assetsStatistics: this.ativos,
        })
        if (r && r.status === 202 && r.value && r.value.request_id) {
          this.compareRequestId = r.value.request_id
          this.startComparePolling()
          this.$showSnack({
            text: 'Comparando PDF. Isso pode demorar alguns minutos.',
            button: true,
            color: 'success',
          })
        } else {
          this.$showSnackModal(r)
          this.loadingComparePdf = false
        }
      } catch (e) {
        this.loadingComparePdf = false
        this.$showSnackModal({
          status: 500,
          message: 'Erro ao iniciar comparação de PDF',
        })
      }
    },
    startComparePolling() {
      this.stopComparePolling()
      this.comparePollingInterval = setInterval(() => {
        this.pollCompareStatus()
      }, 5000)
    },
    stopComparePolling() {
      if (this.comparePollingInterval) {
        clearInterval(this.comparePollingInterval)
        this.comparePollingInterval = null
      }
    },
    async pollCompareStatus() {
      if (!this.compareRequestId) {
        this.stopComparePolling()
        return
      }

      const r = await apiRequisicoes.getStatus({
        requestId: this.compareRequestId,
      })

      if (!r || r.status !== 200 || !r.value) return

      const req = r.value

      if (req.status === 'concluido') {
        this.stopComparePolling()
        this.loadingComparePdf = false

        const attachments = req.attachments_returned
          ? typeof req.attachments_returned === 'string'
            ? JSON.parse(req.attachments_returned)
            : req.attachments_returned
          : []

        if (attachments.length) {
          for (let i = 0; i < attachments.length; i++) {
            try {
              const dr = await apiRequisicoes.download({
                requestId: this.compareRequestId,
                attachmentIndex: i,
              })
              if (dr && dr.status === 200 && dr.value) {
                downloadBase64(
                  dr.value.base64,
                  dr.value.name,
                  dr.value.extension
                )
              }
            } catch (e) {
              // silently fail individual download
            }
          }
          this.$showSnackModal({
            status: 200,
            message: 'Comparação concluída e arquivo baixado!',
          })
        } else {
          this.$showSnackModal({
            status: 200,
            message: 'Comparação concluída com sucesso!',
          })
        }
      } else if (req.status === 'erro') {
        this.stopComparePolling()
        this.loadingComparePdf = false
        this.$showSnackModal({
          status: 500,
          message: req.message || 'Erro ao processar comparação de PDF',
        })
      }
    },
  },
}
</script>

<template v-slot:default="{ busLeft }">
  <div class="carteira-analise">
    <ConditionalTeleport to="page-header-actions" :routes="moduleRoutes">
      <BasePageHeader>
        <template v-slot:filter>
          <WalletFilters
            :fields="walletFilterFields"
            :enabled-field-keys="menuWalletFilterKeys"
            :disabled="isLoadingData"
            @filter="handleFilterApply"
          />
        </template>
        <template v-slot:actions>
          <BaseSplitButton
            v-if="showReportActions"
            label="Relatórios"
            leading="document-text"
            :items="reportActions"
            :loading="isLoadingData"
            @click="openSelectModelPDF"
            @select="onReportAction"
          />
        </template>
      </BasePageHeader>
    </ConditionalTeleport>

    <WalletAlerts
      v-if="walletScope.ativo"
      :context="walletAlertsContext"
      class="mt-5"
      @action="onWalletAlertAction"
    />

    <WalletEmptyState
      v-if="showWalletEmptyState"
      :loading="isLoadingData"
      :client-not-selected="clientNotSelected"
      :clients-loading="loadingClients"
      :has-selected-client="hasSelectedClient"
      :has-access="hasAcess"
      :has-clients="hasClients"
      :has-assets="!isEmpty"
      :has-client="hasClient"
      :is-client-user="isClient"
      :view-tipo="walletScope.viewTipo"
      :grupo="walletScope.grupo"
      @select-client="openSelectClientModal"
      @open-new-client="openRegisterClientModal"
      @new-group="abrirNovoGrupo"
      @new-asset="openRegisterAssetModal"
      @import-assets="openImportExtratoModal"
    />

    <template v-else>
      <div
        v-if="isLoadingData || !isEmpty"
        id="pdf-content"
        ref="pdf-content"
        class="mt-5"
      >
        <!-- Page 1 -->

        <div class="print-only print-break-after align-items-center">
          <FeedbackWrapper
            :loading="walletAnalysisLoading"
            :minHeight="280"
            :message="noDataMessage"
            :noData="!hasIndicatorsData"
            bordered
          >
            <div class="carteira-analise__card az-card-bordered">
              <div class="d-flex justify-space-between mb-3">
                <div class="carteira-analise__title az-ds-card-title">
                  Indicadores
                </div>

                <div class="d-flex align-center">
                  <v-tooltip v-if="clienteBenchmarkMeta" bottom max-width="260">
                    <template v-slot:activator="{ on }">
                      <div class="d-inline-flex" v-on="on">
                        <div
                          v-if="
                            benchmarks_selecionados.includes(
                              CLIENT_BENCHMARK_KEY
                            )
                          "
                          class="d-inline-flex align-center mr-2"
                        >
                          <BaseStatus
                            text
                            preserve-case
                            :content="clienteBenchmarkMeta.label"
                            :color="BENCHMARK_PERSONALIZADO_COLOR"
                          />
                          <icon-svg
                            name="mdi-close"
                            :size="16"
                            color="var(--secondary)"
                            role="button"
                            tabindex="0"
                            aria-label="Remover benchmark do cliente"
                            class="carteira-analise__remove-benchmark ml-1"
                            @click="removeClienteBenchmarkFromIndicadores"
                            @keydown.enter="
                              removeClienteBenchmarkFromIndicadores
                            "
                            @keydown.space.prevent="
                              removeClienteBenchmarkFromIndicadores
                            "
                          />
                        </div>
                        <BaseButton
                          v-else
                          secondary
                          x-small
                          class="mr-2"
                          @click="addClienteBenchmarkToIndicadores"
                        >
                          + {{ clienteBenchmarkMeta.label }}
                        </BaseButton>
                      </div>
                    </template>
                    <span
                      >Benchmark padrão deste cliente. Para alterar, edite os
                      dados cadastrais do cliente.</span
                    >
                  </v-tooltip>
                  <AutocompleteMany
                    style="max-width: 275px"
                    :items="BENCHMARKS"
                    text="nome"
                    label="Selecione os indicadores"
                    :multiple="true"
                    :selected.sync="benchmarks_selecionados"
                    :selected-items-to-show="2"
                    @update:selected="benchmark_statistics_change = true"
                  />
                </div>
              </div>
              <TabelaRentBenchmark
                :rentabilidades-all="rentBenchmarkTable.rentabilidadesAll"
                :benchmarksSelecionados="benchmarks_selecionados"
                :rentabilidades-benchmarks="
                  rentBenchmarkTable.rentabilidadesBenchmarks
                "
                :clienteBenchmarkMeta="clienteBenchmarkMeta"
              />
              <p v-if="dataFimReferencia" class="carteira-analise__text-base">
                * Data base para a rentabilidade: {{ dataFimReferencia }}
              </p>
            </div>
          </FeedbackWrapper>

          <!-- Gráfico de composição estratégica -->
          <div class="mt-5">
            <AlocacaoEstrategica
              :loading="walletAnalysisLoading"
              :valuesChart="alocacaoChartValues"
              :optionsChat="alocacaoChartOpts"
              :colorsByLabel="allocationColorsByLabel"
              :investmentType="displayInvestmentTypeFilter"
              :suggestedValues="suggestedAllocations"
            >
              <template v-slot:info-adicional>
                <p v-if="dataFimReferencia" class="carteira-analise__text-base">
                  * Data base para a composição estratégica:
                  {{ dataFimReferencia }}
                </p>
              </template>
            </AlocacaoEstrategica>
          </div>

          <!-- Gráfico de portfólio - Bugando altura e largura do grafico -->
          <div class="mt-5 print-grafico-linha">
            <PortfolioBenchmark
              v-model="benchmarks_selecionados_grafico"
              :data="rentabilityChart"
              :loading="walletAnalysisLoading"
              :benchmarks="BENCHMARKS"
              :cliente-benchmark-meta="clienteBenchmarkMeta"
              :available-benchmark-names="availableBenchmarkNames"
              :show-add-benchmark="!isClient"
              :empty-message="noDataMessage"
              @change="benchmark_graph_changed = true"
              @add-benchmark="openBenchmark"
              @reset="resetGraphBenchmarks"
            >
              <template v-slot:footer>
                <p v-if="dataFimReferencia" class="carteira-analise__text-base">
                  * Data base para o portfólio:
                  {{ dataFimReferencia }}
                </p>
              </template>
            </PortfolioBenchmark>
          </div>
        </div>

        <v-row class="mt-2">
          <v-col cols="12" lg="6">
            <!-- Page 1.7 -->
            <Distribuicao
              distribuicaoPor="Instituição"
              :data="institutionDistributionData"
              :loading="walletAnalysisLoading"
              :investmentType="displayInvestmentTypeFilter"
              :minHeight="385"
            />
          </v-col>

          <v-col cols="12" lg="6">
            <!-- Page 1.5 -->
            <LiquidezChart
              :liquidezData="liquidezChartData"
              :loading="walletAnalysisLoading"
              :empty-message="noDataMessage"
              :min-height="385"
              class="print-only print-break-after"
            />
          </v-col>
        </v-row>

        <!-- Page 2 -->
        <div class="mt-2 print-only print-break-before align-items-center">
          <FeedbackWrapper
            :loading="isLoadingData"
            :minHeight="300"
            :message="noDataMessage"
            :noData="
              !walletScope.ativo ||
              (!Object.keys(retornoMensalTable).length && isEmpty)
            "
            bordered
          >
            <div class="carteira-analise__card az-card-bordered">
              <div class="carteira-analise__title az-ds-card-title">
                Retornos mensais
              </div>

              <div class="my-3">
                <div
                  style="margin-bottom: 60px; max-width: 100%; overflow: auto"
                >
                  <TabelaRetornoMensais
                    :retorno-mensal="retornoMensalTable"
                    :cliente-benchmark-meta="clienteBenchmarkMeta"
                  />
                </div>
              </div>
            </div>
          </FeedbackWrapper>
        </div>

        <FeedbackWrapper
          :loading="walletAnalysisLoading"
          :minHeight="385"
          :message="noDataMessage"
          :noData="!Object.entries(assetsAnalysisData).length"
          class="mt-5"
          bordered
        >
          <AssetsAnalysisTable
            lazyMode
            :show-owner-column="walletScope.tipo === 'grupo'"
            :assets="assetsAnalysisData"
            :group-assets.sync="groupAssets"
            :loading-assets="loadingAssets"
            :class-benchmark-map="classBenchmarkMap"
            :data-reference-end="dataFimReferencia"
            hide-group-assets-toggle
            @reload-assets="reloadAssets"
          />
        </FeedbackWrapper>
      </div>
    </template>

    <ConsolidatorSelectClient />

    <InputByType
      type="file"
      id-input="compare-pdf-input"
      style="display: none"
      @add-file="handleComparePdfFile"
    />
  </div>
</template>

<style lang="sass">
.carteira-analise
  &__card
    .v-autocomplete
      margin-top: 0px !important

  &__switch
    .v-input--selection-controls
      margin: 0px !important
    padding-bottom: 16px

  &__remove-benchmark
    cursor: pointer

  &__text-base
    font-size: 14px
    font-weight: 600 !important
    color: var(--secondary) !important
    margin-top: 16px

  &__alert
    margin-bottom: 32px
    border-radius: 10px !important

  &__no-data
    display: flex
    flex-direction: column
    justify-content: center
    align-items: center
    padding-block: 60px !important
    margin-top: 32px

    &-title
      margin-bottom: 32px

    &-description
      max-width: 725px
      text-align: center
      margin-bottom: 32px

    &-actions
      display: flex
      gap: 24px
</style>

<style type="text/css">
.border-top-headers {
  border-top: 1.25px solid var(--primary);
}

.border-bottom-thead {
  border-bottom: 1.25px solid var(--primary) !important;
}

.cards {
  background-color: white;
  border: 1px solid var(--primary);
  height: 17vw;
  width: 14vw;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  display: flex;
}

@media screen {
  .show-on-print {
    display: none;
  }
}

@media print {
  @page {
    size: auto; /* auto is the initial value */
    margin: 0mm; /* this affects the margin in the printer settings */
  }

  * {
    display: none;
  }

  html {
    background-color: #ffffff;
    margin: 0px; /* this affects the margin on the html before sending to printer */
  }

  body {
    /* border: solid 1px blue; */
    /* margin: 10mm 15mm 10mm 15mm; margin you want for the content */
  }

  .dont-show-print {
    display: none !important;
  }

  .print-only {
    display: block !important;
    position: relative;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    /* width: 100%; */
    width: 1000px;
    margin-left: -115px;
    z-index: 999999999;
    background-color: #ffffff;
  }

  .show-on-print {
    display: block !important;
  }

  .print-break-after {
    page-break-after: always;
  }

  .print-break-before {
    page-break-before: always;
  }

  .print-header-page {
    display: none;
    background-color: black !important;
    -webkit-print-color-adjust: exact;
    color: white;
    padding: 15px;
    display: flex !important;
    align-items: center;
    justify-content: space-between;
    border-radius: 6px;
    border-bottom: 4px solid var(--primary);
    text-transform: uppercase;
    margin-bottom: 60px;
  }

  .print-header-page img {
    max-width: 154px;
    max-height: 90px;
    margin-right: 20px;
  }

  .print-header-page h1 {
    font-size: 1.5em;
  }

  .my-wallet__block {
    box-shadow: none;
  }

  .print-grafico-linha {
    width: 570px;
  }

  canvas.chartjs-render-monitor {
    min-height: 100%;
    max-width: 100%;
    max-height: 100%;
    height: auto !important;
    width: auto !important;
  }

  table {
    max-width: 1000px;
    width: 1000px;
  }
}
</style>
